import type {
  AuthMeResponse,
  AuthSessionResponse,
  EmailLoginRequest,
  EmailRegisterRequest,
} from '@shared/auth';

import { useAuthStore } from '../store/useAuthStore';

const BASE = '/api/v1/auth';

interface Envelope<T> {
  sessionState: string;
  success: boolean;
  data: T | null;
  messages: Array<{ severity: string; message: string; details?: Record<string, unknown> }>;
}

export class AuthApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { csrf?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  const isMutating = init.method !== undefined && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(init.method.toUpperCase());
  if (init.csrf ?? isMutating) {
    const csrf = useAuthStore.getState().csrfToken;
    if (csrf) headers.set('X-CSRF-Token', csrf);
  }
  const res = await fetch(BASE + path, {
    ...init,
    headers,
    credentials: 'include',
  });
  const body = (await res.json()) as Envelope<T>;
  if (!res.ok || !body.success) {
    const msg = body.messages[0];
    const code = (msg?.details?.code as string | undefined) ?? 'unknown_error';
    throw new AuthApiError(res.status, code, msg?.message ?? 'Request failed', msg?.details);
  }
  if (body.data === null) {
    throw new AuthApiError(res.status, 'empty_data', 'Empty response data');
  }
  return body.data;
}

export const authApi = {
  guest: (body?: { fingerprint_hash?: string | undefined }) =>
    request<AuthSessionResponse>('/guest', {
      method: 'POST',
      csrf: false,
      ...(body ? { body: JSON.stringify(body) } : {}),
    }),

  register: (body: EmailRegisterRequest) =>
    request<AuthSessionResponse>('/register', { method: 'POST', body: JSON.stringify(body), csrf: false }),

  login: (body: EmailLoginRequest) =>
    request<AuthSessionResponse>('/login', { method: 'POST', body: JSON.stringify(body), csrf: false }),

  logout: () => request<{ logged_out: boolean }>('/logout', { method: 'POST' }),

  me: () => request<AuthMeResponse>('/me', { method: 'GET' }),

  changeNickname: (nickname: string) =>
    request<{ nickname: string }>('/nickname', { method: 'POST', body: JSON.stringify({ nickname }) }),

  googleRedirectUrl: () => `${BASE}/google/redirect`,
};
